/**
 * Jeans Score — forward-looking "how will your next message feel?"
 *
 * Unlike the ChatGPT Tax (backward: time already wasted), this predicts
 * the experience quality of continuing each conversation.
 *
 * Three components, each normalized to 0–1:
 *
 * 1. **Drag** — estimated render delay for the next response, based on
 *    current DOM size and average response complexity (including images).
 * 2. **Stretch** — risk of needing to re-instruct or re-establish context,
 *    based on observed reinstruction rate and topic restart patterns.
 * 3. **Spare** — risk of the next message going unheard, based on how
 *    close the conversation is to context overflow (bloat score).
 *
 * Composite: dragScore × W_drag + stretchScore × W_stretch + spareScore × W_spare
 *
 * @module jeans-score
 */

/** @typedef {import('./types.js').ConversationAnalysis} ConversationAnalysis */
/** @typedef {import('./types.js').CrossChatAnalysis} CrossChatAnalysis */

import { estimateRenderDelay } from './perceived-wait-estimator.js';

// ── Normalization ceilings ──────────────────────────────────────────

/** Render delay (seconds) for next message that scores 1.0 */
const DRAG_CEILING_SEC = 60;

// ── Image render weight ─────────────────────────────────────────────

/**
 * Render-char equivalent per image in the conversation.
 * Images involve decode + layout reflow but don't have a character count.
 * Calibrated relative to code (12×) and table (18×) weights — an image
 * is roughly equivalent to a 300-char code block in render cost.
 */
const IMAGE_RENDER_CHARS = 3600;

// ── Component weights (must sum to 1) ───────────────────────────────

const WEIGHT_DRAG = 1 / 3;
const WEIGHT_STRETCH = 1 / 3;
const WEIGHT_SPARE = 1 / 3;

/**
 * Compute the jeans score for a single conversation.
 *
 * @param {ConversationAnalysis} conv
 * @param {CrossChatAnalysis} crossChat
 * @returns {{ score: number, dragScore: number, stretchScore: number, spareScore: number, nextRenderSec: number }}
 */
export function computeJeansScore(conv, crossChat) {
  // ── Drag: next-message render estimate ──────────────────────────
  //
  // Given current DOM size (messageCount) and the average response
  // complexity in this chat, how long will the next response render?

  const assistantCount = conv.assistantMessageCount || 1;
  const textRenderChars = conv.renderProfile?.adjustedRenderChars || 0;
  const imageRenderChars = (conv.imageCount || 0) * IMAGE_RENDER_CHARS;
  const avgResponseRenderChars = (textRenderChars + imageRenderChars) / assistantCount;

  const nextRenderSec = estimateRenderDelay(conv.messageCount, avgResponseRenderChars);
  const dragScore = Math.min(1, nextRenderSec / DRAG_CEILING_SEC);

  // ── Stretch: re-instruction + restart risk ──────────────────────
  //
  // How likely is the model to need re-instruction on the next turn,
  // or is this a topic the user keeps restarting in new chats?

  const reinstructionRate = conv.reinstruction
    ? conv.reinstruction.reinstructionRate
    : 0;

  let restartRisk = 0;
  for (const group of crossChat.topicRestarts.restartGroups) {
    if (group.conversationIds.includes(conv.conversationId)) {
      restartRisk = Math.max(restartRisk, (group.count - 1) / group.count);
    }
  }

  // Either signal alone indicates stretch risk
  const stretchScore = Math.min(1, Math.max(reinstructionRate, restartRisk));

  // ── Spare: context overflow risk ────────────────────────────────
  //
  // bloatScore is the best forward predictor of whether the next
  // message will go unheard. At 1.0 the window is full. Uncapped —
  // scores above 1.0 push the composite past 1.0 into Blown Out.

  const spareScore = conv.bloatScore;

  // ── Weighted composite ──────────────────────────────────────────

  const score = dragScore * WEIGHT_DRAG
    + stretchScore * WEIGHT_STRETCH
    + spareScore * WEIGHT_SPARE;

  return { score, dragScore, stretchScore, spareScore, nextRenderSec };
}
